import { useEffect, useId, useState, useSyncExternalStore } from 'react'
import { Input } from './Input'

/**
 * Deutsche Datums-/Zeitfelder (browserunabhängig, 24 h): Anzeige und Eingabe als TT.MM.JJJJ / HH:MM.
 * Der Wert zur API bleibt unverändert: Datum „JJJJ-MM-TT“, Zeit „HH:MM“, Datum+Zeit „JJJJ-MM-TTTHH:MM“ (wie datetime-local).
 */
type Art = 'datum' | 'zeit' | 'datumzeit'
const LAENGE: Record<Art, number> = { datum: 8, zeit: 4, datumzeit: 12 }
const PLATZHALTER: Record<Art, string> = { datum: 'TT.MM.JJJJ', zeit: 'HH:MM', datumzeit: 'TT.MM.JJJJ HH:MM' }

/** Ziffern in die Anzeigeform bringen (Trennzeichen automatisch). */
function maskiere(ziffern: string, art: Art): string {
  const d = ziffern.slice(0, LAENGE[art])
  const teile: string[] = []
  if (art !== 'zeit') {
    teile.push(d.slice(0, 2), d.slice(2, 4), d.slice(4, 8))
    const datum = [teile[0], teile[1], teile[2]].filter((t, i) => t || (i === 0 && false)).join('.')
    const rest = art === 'datumzeit' ? d.slice(8) : ''
    const zeit = rest ? [rest.slice(0, 2), rest.slice(2, 4)].filter(Boolean).join(':') : ''
    return zeit ? `${datum} ${zeit}` : datum
  }
  return [d.slice(0, 2), d.slice(2, 4)].filter(Boolean).join(':')
}

/** Anzeigetext -> API-Wert; '' = leer, null = unvollständig/ungültig. */
function zuWert(text: string, art: Art): string | null {
  const z = text.replace(/\D/g, '')
  if (!z) return ''
  if (z.length !== LAENGE[art]) return null
  const zahl = (a: number, b: number) => Number(z.slice(a, b))
  let datum = '', zeit = ''
  if (art !== 'zeit') {
    const t = zahl(0, 2), m = zahl(2, 4), j = zahl(4, 8)
    const dt = new Date(j, m - 1, t)
    if (j < 1900 || dt.getFullYear() !== j || dt.getMonth() !== m - 1 || dt.getDate() !== t) return null
    datum = `${String(j).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(t).padStart(2, '0')}`
  }
  if (art !== 'datum') {
    const o = art === 'zeit' ? 0 : 8
    const h = zahl(o, o + 2), mi = zahl(o + 2, o + 4)
    if (h > 23 || mi > 59) return null
    zeit = `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`
  }
  return art === 'datum' ? datum : art === 'zeit' ? zeit : `${datum}T${zeit}`
}

/** API-Wert -> Anzeigetext (akzeptiert auch Sekunden/Zeitzone am Ende, die ignoriert werden). */
function zuAnzeige(wert: string, art: Art): string {
  if (!wert) return ''
  const m = wert.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/)
  const nurZeit = wert.match(/^(\d{2}):(\d{2})/)
  if (art === 'zeit') return nurZeit ? `${nurZeit[1]}:${nurZeit[2]}` : m && m[4] ? `${m[4]}:${m[5]}` : ''
  if (!m) return ''
  const datum = `${m[3]}.${m[2]}.${m[1]}`
  return art === 'datum' ? datum : `${datum} ${m[4] ?? '00'}:${m[5] ?? '00'}`
}

/**
 * Zentrale Sperre: Jedes Feld meldet sich hier an, solange sein Text unvollständig/ungültig ist
 * (verschwindet das Feld, wird es automatisch abgemeldet). Formulare lesen sie mit useDatumUngueltig().
 */
const ungueltigeFelder = new Set<string>()
const lauscher = new Set<() => void>()
let ungueltigAnzahl = 0
function meldeUngueltig(id: string, ungueltig: boolean) {
  if (ungueltig === ungueltigeFelder.has(id)) return
  if (ungueltig) ungueltigeFelder.add(id); else ungueltigeFelder.delete(id)
  ungueltigAnzahl = ungueltigeFelder.size
  lauscher.forEach(l => l())
}
export const DATUM_UNGUELTIG_HINWEIS = 'Bitte ein gültiges Datum/eine gültige Uhrzeit eintragen oder das Feld leeren.'
/** true, solange irgendein eingeblendetes Datums-/Zeitfeld unvollständig oder ungültig ist -> Speichern sperren. */
export function useDatumUngueltig(): boolean {
  return useSyncExternalStore(
    cb => { lauscher.add(cb); return () => { lauscher.delete(cb) } },
    () => ungueltigAnzahl > 0,
    () => false,
  )
}

interface Props {
  value: string
  onChange: (wert: string) => void
  /** Meldet, ob der getippte Text unvollständig/ungültig ist (dann liefert onChange ''), z. B. um Speichern zu sperren. */
  onUngueltig?: (ungueltig: boolean) => void
  disabled?: boolean
  className?: string
  id?: string
  name?: string
  'aria-label'?: string
}

function Feld({ art, value, onChange, onUngueltig, disabled, className, id, name, ...rest }: Props & { art: Art }) {
  const [text, setText] = useState(() => zuAnzeige(value, art))
  const [ungueltig, setUngueltig] = useState(false)
  const feldId = useId()
  useEffect(() => () => meldeUngueltig(feldId, false), [feldId])
  // Wert von außen geändert (z. B. Formular geladen): Anzeige nachziehen, solange er nicht dem Getippten entspricht
  useEffect(() => {
    const w = zuWert(text, art)
    if (w !== value && !(w === null && value === '')) { setText(zuAnzeige(value, art)); setUngueltig(false); meldeUngueltig(feldId, false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <div className="w-full">
      <Input
        id={id} name={name} aria-label={rest['aria-label']}
        type="text" inputMode="numeric" autoComplete="off"
        placeholder={PLATZHALTER[art]} maxLength={art === 'datumzeit' ? 16 : 10}
        value={text} disabled={disabled} className={className}
        aria-invalid={ungueltig || undefined}
        onChange={e => {
          const roh = e.target.value
          const ziffern = roh.replace(/\D/g, '')
          // Rücktaste über ein Trennzeichen: Maske würde es sofort wieder einfügen -> eine Ziffer mehr entfernen
          const neu = maskiere(roh.length < text.length && ziffern === text.replace(/\D/g, '') ? ziffern.slice(0, -1) : ziffern, art)
          setText(neu)
          const w = zuWert(neu, art)
          setUngueltig(w === null && neu.replace(/\D/g, '').length === LAENGE[art])
          meldeUngueltig(feldId, w === null)
          onUngueltig?.(w === null)
          onChange(w ?? '')
        }}
        onBlur={() => { const w = zuWert(text, art); if (w !== null) setText(zuAnzeige(w, art)); else if (text) setUngueltig(true) }}
      />
      {ungueltig && <p role="alert" className="mt-1 text-xs text-amber-300">Ungültig – Format {PLATZHALTER[art]}</p>}
    </div>
  )
}

export const DatumZeitFeld = (p: Props) => <Feld art="datumzeit" {...p} />
export const DatumFeld = (p: Props) => <Feld art="datum" {...p} />
export const ZeitFeld = (p: Props) => <Feld art="zeit" {...p} />

/** Für Tests/Anzeige: API-Wert -> deutscher Text. */
export const datumZeitText = (w: string) => zuAnzeige(w, 'datumzeit')
